/**
 * The interface language, chosen per device like the theme (R1-09e). Pure so it can be unit-tested; the
 * React wiring lives in `i18n/provider.tsx`.
 */
export type Locale = "id" | "en";

export const LOCALES: readonly Locale[] = ["id", "en"];
export const LOCALE_STORAGE_KEY = "kopi-locale";

/**
 * The `Intl` locale numbers and dates are formatted in. English uses en-GB rather than en-ID: CLDR's
 * en-ID groups digits with "." (Rp45.000), which an English reader takes for a decimal point.
 */
export const INTL_LOCALE: Record<Locale, string> = { id: "id-ID", en: "en-GB" };

/** The slice of `Storage` used here, so tests can pass a stand-in. */
export type LocaleStorage = Pick<Storage, "getItem" | "setItem">;

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/**
 * The stored choice; on a first visit (or with unreadable storage) English when the browser's preferred
 * language is English, otherwise Indonesian.
 */
export function readLocale(storage: LocaleStorage | undefined, languages: readonly string[]): Locale {
  try {
    const stored = storage?.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Private mode / blocked storage: follow the browser.
  }
  return /^en\b/i.test(languages[0] ?? "") ? "en" : "id";
}

/** Persists the choice; a failing storage only loses persistence, never the in-page language. */
export function writeLocale(storage: LocaleStorage | undefined, locale: Locale): void {
  try {
    storage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore: the language still applies for this page load.
  }
}
